/**
 * GryKriCaPopup — property-centric popup for the GryKriCa Comm.V entity.
 *
 * Layout (Final Plan §10.3):
 *   ┌─ Header (name, ownership, headline numbers, Accounting/Economic toggle) ─┐
 *   │ [ Properties ▸ default ]  [ Balance sheet ]  [ Cash & P&L ]              │
 *   └──────────────────────────────────────────────────────────────────────────┘
 *
 * Properties tab → 5 cards (one per property) → click → PropertyDetailModal
 *   with 4 sub-tabs (Overview / Tenant & rent / Mortgage / P&L).
 *
 * Data sources:
 *   GET /api/entities/grykrica/view?view=accounting|economic
 *   GET /api/entities/grykrica/transactions?property_code=&kind=&from=&to=
 *   GET /api/entities/grykrica/cashflow-summary?from=&to=
 *
 * Loaded from InvestmentList.js when the user clicks the GryKRYCA card.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { apiGet } from '../../utils/api';
import './GryKriCaPopup.css';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtEur = (v, decimals = 0) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v < 0 ? '-' : '';
  return `${sign}€${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals })}`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtMonthYear = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};
const fmtPct = (v, decimals = 1) => {
  if (v == null || isNaN(v)) return '—';
  return `${v.toFixed(decimals)}%`;
};

// Sum tx amounts in EUR
const sum = (arr, key = 'amount_eur') => (arr || []).reduce((s, x) => s + (x[key] || 0), 0);

// Group an array by a key function
const groupBy = (arr, keyFn) => {
  const out = {};
  for (const x of arr) {
    const k = keyFn(x);
    if (!out[k]) out[k] = [];
    out[k].push(x);
  }
  return out;
};

// ─── Payment status — 12mo window, NO splitting, rent-schedule aware ──────────
// Algorithm (per KG 22-Jun-2026):
//   1. Build a 12-row month grid.
//   2. For each month, expected rent comes from the tenant's rent_schedule
//      (handles step-ups like Hollestraat €1,250 → €1,528, and multi-tenant
//       periods like Gentsestraat Banze €650 → free March → Espace €730).
//   3. Each payment maps to exactly ONE month — never split across months.
//      Tolerance ±15% of expected for that period qualifies a payment as "rent".
//      Out-of-tolerance payments are tagged as "extras" (utility recovery,
//      partial cost passthrough) and listed separately, NOT a rent payment.
//   4. Attribution rules (in order):
//      a. If tenant has explicit payment_attribution_overrides → use them
//         (Pascal: 28-Sep batch covered May-Sep 2025; 9-Apr batch covered
//         Oct-Dec 2025; 31-May covered May 2026).
//      b. Else: same-day multi-wire batches go to oldest unmatched months in order.
//      c. Single payment with date in month X and matching expected for month X
//         → assigned to X. If X is already matched, fall back to oldest unmatched.
//   5. A month is "current" (not unpaid) if today is still in its rent window.
function expectedRentForMonth(rentSchedule, year, month, fallback) {
  if (!rentSchedule?.length) return fallback || 0;
  const ymStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  let current = fallback || 0;
  for (const step of rentSchedule) {
    if (step.from <= ymStr) current = step.monthly_rent;
    else break;
  }
  return current;
}

function ymKey(year, month) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function computePaymentStatus(rentTxs, tenant) {
  const now = new Date();
  const months = [];
  const schedule = tenant?.rent_schedule;
  const fallbackRent = tenant?.monthly_rent_eur || 0;
  const overrides = tenant?.payment_attribution_overrides || [];

  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const expected = schedule ? expectedRentForMonth(schedule, d.getFullYear(), d.getMonth(), fallbackRent) : fallbackRent;
    months.push({
      year:  d.getFullYear(),
      month: d.getMonth(),
      key:   ymKey(d.getFullYear(), d.getMonth()),
      label: d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
      expected,
      paid_by:    null,         // single tx that satisfies this month
      status:     expected === 0 ? 'free' : 'unpaid',
      is_current: i === 0,
    });
  }

  if (!fallbackRent && !schedule) {
    return { months, owed: 0, monthsUnpaid: 0, monthsCurrent: 0, paidUpTo: null,
             lastPayment: null, rentPayments: [], extras: [], totalPaid12mo: 0, totalExpected12mo: 0 };
  }

  const windowStart = new Date(months[0].year, months[0].month, 1);
  const inWindow = (rentTxs || [])
    .filter(t => new Date(t.booking_date) >= windowStart)
    .sort((a, b) => new Date(a.booking_date) - new Date(b.booking_date));

  // (1) Classify each in-window payment as rent vs extra (using expected
  //     for the calendar month of payment as the reference)
  const rentPayments = [];
  const extras = [];
  for (const tx of inWindow) {
    const txDate = new Date(tx.booking_date);
    const refExpected = schedule ? expectedRentForMonth(schedule, txDate.getFullYear(), txDate.getMonth(), fallbackRent) : fallbackRent;
    const tolerance = Math.max(refExpected * 0.15, 50);
    if (refExpected > 0 && Math.abs(tx.amount_eur - refExpected) <= tolerance) {
      rentPayments.push(tx);
    } else {
      extras.push(tx);
    }
  }

  // (2) Apply explicit overrides FIRST. Crucially: any payment listed in an
  //     override is marked consumed even when the target month is out-of-window
  //     (those months are pre-window-cleared, no need to attribute again).
  const consumed = new Set();
  for (const ov of overrides) {
    const sameDay = rentPayments.filter(t => new Date(t.booking_date).toISOString().slice(0, 10) === ov.booking_date);
    sameDay.forEach((t, idx) => {
      const monthKey = ov.attribute_to_months[idx];
      if (!monthKey) return;            // more wires than override entries → handled below
      consumed.add(t);                  // ← key fix: consume regardless of window membership
      const target = months.find(m => m.key === monthKey);
      if (target && target.paid_by === null) {
        target.paid_by = t;
        target.status = 'paid';
      }
      // else: target month is outside the 12-mo window → silently cleared
    });
  }

  // Group remaining (non-consumed) payments by day
  const byDay = {};
  for (const tx of rentPayments) {
    if (consumed.has(tx)) continue;
    const k = new Date(tx.booking_date).toISOString().slice(0, 10);
    if (!byDay[k]) byDay[k] = [];
    byDay[k].push(tx);
  }

  // (3) Same-day multi-wire batches without explicit override:
  //     walk BACKWARD from the payment date — each wire fills the most-recent
  //     unmatched month before/at the payment date. This stops EVB's 20-May
  //     batch from bleeding into Banze's old Sep-25 miss.
  for (const k of Object.keys(byDay).sort()) {
    if (byDay[k].length <= 1) continue;     // single days handled below
    const paymentDate = new Date(k);
    // Start the cursor at the END of the payment's own month, not the exact day,
    // so the current month is fillable. (A €730 batch booked 20-May covers May +
    // April — using the bare 20-May date wrongly skipped May, because 31-May > 20-May,
    // and spilled the 2nd wire back into February.)
    let cursorEnd = new Date(paymentDate.getFullYear(), paymentDate.getMonth() + 1, 0);
    for (const tx of byDay[k]) {
      // Find latest unmatched month with end-of-month ≤ cursorEnd, AND whose
      // expected rent matches this wire's amount (±15%). The amount guard keeps a
      // €730 Espace wire from landing on a €650 Banze month — i.e. a tenant's
      // catch-up batch stays inside that tenant's own months.
      let target = null;
      for (let i = months.length - 1; i >= 0; i--) {
        const m = months[i];
        const monthEnd = new Date(m.year, m.month + 1, 0);
        if (monthEnd > cursorEnd) continue;
        if (m.paid_by !== null || m.status === 'free') continue;
        const tol = Math.max(m.expected * 0.15, 50);
        if (m.expected > 0 && Math.abs(tx.amount_eur - m.expected) > tol) continue;
        target = m;
        break;
      }
      if (!target) break;
      target.paid_by = tx;
      target.status = 'paid';
      consumed.add(tx);
      // Move cursor to last day of the month BEFORE the matched month
      cursorEnd = new Date(target.year, target.month, 0);
    }
  }

  // (4) Single-payment days:
  //     prefer calendar month of payment; else nearest unmatched within ±3 mo.
  for (const k of Object.keys(byDay).sort()) {
    if (byDay[k].length !== 1) continue;
    const tx = byDay[k][0];
    if (consumed.has(tx)) continue;
    const txDate = new Date(tx.booking_date);
    const monthKey = ymKey(txDate.getFullYear(), txDate.getMonth());
    const direct = months.find(m => m.key === monthKey && m.paid_by === null && m.status !== 'free');
    if (direct) {
      direct.paid_by = tx;
      direct.status = 'paid';
      consumed.add(tx);
      continue;
    }
    // Search ±3 months for nearest unmatched (avoids jumping years), and only
    // onto a month whose expected rent matches this wire's amount (±15%).
    let best = null, bestDist = 999;
    for (const m of months) {
      if (m.paid_by !== null || m.status === 'free') continue;
      const tol = Math.max(m.expected * 0.15, 50);
      if (m.expected > 0 && Math.abs(tx.amount_eur - m.expected) > tol) continue;
      const dist = Math.abs((m.year - txDate.getFullYear()) * 12 + (m.month - txDate.getMonth()));
      if (dist <= 3 && dist < bestDist) { bestDist = dist; best = m; }
    }
    if (best) {
      best.paid_by = tx;
      best.status = 'paid';
      consumed.add(tx);
    }
  }

  // (5) Current-month: only mark 'current' if today is still within the
  //     tenant's grace window (median pay day + 7 days). Past that → 'unpaid'.
  const today = new Date();
  const currentMonth = months[months.length - 1];
  if (currentMonth.status === 'unpaid' && currentMonth.expected > 0) {
    const dueDay  = tenant?.median_day_of_month || 15;
    const grace   = dueDay + 7;
    if (today.getDate() <= grace) currentMonth.status = 'current';
    // else: stays 'unpaid' — show in arrears
  }

  // (5b) Mute arrears from departed tenants (KG 23-Jun-2026): any still-unpaid
  //      month explicitly written off (e.g. Banze's Sept-2025 miss on Gentsestraat)
  //      is shown greyed and excluded from "owed" / monthsUnpaid.
  const mutedList = tenant?.arrears_muted_months || [];
  const mutedMap = {};
  mutedList.forEach(x => { const k = typeof x === 'string' ? x : x.month; mutedMap[k] = (typeof x === 'object' && x.reason) || 'Past tenant — written off'; });
  for (const m of months) {
    if (m.status === 'unpaid' && mutedMap[m.key] !== undefined) {
      m.status = 'muted';
      m.muted_reason = mutedMap[m.key];
    }
  }

  // Owed = sum of (expected) for status === 'unpaid' (excluding 'current', 'free', 'muted')
  const owed = months.filter(m => m.status === 'unpaid').reduce((s, m) => s + m.expected, 0);
  const monthsUnpaid = months.filter(m => m.status === 'unpaid').length;
  const monthsCurrent = months.filter(m => m.status === 'current').length;

  // paid_up_to: end of latest 'paid' or 'free' month before any 'unpaid'
  let paidUpTo = null;
  const firstUnpaidIdx = months.findIndex(m => m.status === 'unpaid');
  if (firstUnpaidIdx === -1) {
    const last = months[months.length - 1];
    paidUpTo = new Date(last.year, last.month + 1, 0);
  } else if (firstUnpaidIdx > 0) {
    const m = months[firstUnpaidIdx - 1];
    paidUpTo = new Date(m.year, m.month + 1, 0);
  }

  const lastPayment = rentPayments.length
    ? { date: rentPayments[rentPayments.length - 1].booking_date, amount: rentPayments[rentPayments.length - 1].amount_eur }
    : null;

  return {
    months,
    owed,
    monthsUnpaid,
    monthsCurrent,
    paidUpTo,
    lastPayment,
    rentPayments,
    extras,           // utility recoveries, partial cost passthroughs
    totalPaid12mo: sum(rentPayments),
    totalExpected12mo: months.reduce((s, m) => s + m.expected, 0),
  };
}

// ─── Server-side rent ledger (5 Oct 2026) ─────────────────────────────────────
// When the API has computed `tenant.rent_ledger` (utils/grykricaRentLedger.js —
// full-history FIFO allocation per lease period, incl. Pascal's 15th→15th periods,
// waivers, deposits/OV excluded), prefer it over the 12-month client heuristic.
// Returns the same shape as computePaymentStatus so nothing downstream changes.
function ledgerToStatus(rl) {
  const periods = (rl.periods || []).slice(-12);
  const months = periods.map(p => {
    const s = p.status; // paid | unpaid | partial | current | free
    const by = (p.paid_by || []);
    const paidBy = by.length
      ? { booking_date: by[by.length - 1].booking_date,
          amount_eur: by.reduce((a, b) => a + (b.amount_eur || 0), 0) }
      : null;
    const d = new Date(p.start);
    return {
      year: d.getUTCFullYear(), month: d.getUTCMonth(), key: p.key, label: p.label,
      expected: p.expected,
      paid_by: (s === 'paid' || p.status === 'partial') ? paidBy : null,
      status: s, is_current: s === 'current',
      waived: p.waived || 0, waive_note: p.waive_note || '',
    };
  });
  const rentPayments = [];
  for (const p of (rl.periods || [])) for (const b of (p.paid_by || [])) rentPayments.push({ booking_date: b.booking_date, amount_eur: b.amount_eur, description: b.description });
  return {
    months,
    owed: rl.owed || 0,
    monthsUnpaid: rl.monthsUnpaid || 0,
    monthsCurrent: rl.monthsCurrent || 0,
    paidUpTo: rl.paidUpTo ? new Date(rl.paidUpTo) : null,
    lastPayment: rl.lastPayment || null,
    rentPayments,
    extras: (rl.excluded || []).map(x => ({ booking_date: x.booking_date, amount_eur: x.amount_eur, classifier_note: x.note })),
    totalPaid12mo: periods.reduce((s, p) => s + (p.paid || 0), 0),
    totalExpected12mo: periods.reduce((s, p) => s + (p.expected || 0), 0),
    openPeriods: rl.open_periods || [],
    ledgerAsOf: rl.as_of || null,
    fromServerLedger: true,
  };
}

function statusFor(rentTxs, tenant) {
  if (tenant?.rent_ledger?.periods?.length) return ledgerToStatus(tenant.rent_ledger);
  return computePaymentStatus(rentTxs, tenant);
}

// ─── Modal scaffold ───────────────────────────────────────────────────────────
function ModalOverlay({ onClose, children, className = '' }) {
  return (
    <div className="grk-overlay" onClick={onClose}>
      <div className={`grk-modal ${className}`} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ─── Property card (Properties tab row) ───────────────────────────────────────
function PropertyCard({ position, view, statusByProperty, onClick }) {
  const detail = position.detail || {};
  const tenant = detail.tenant || {};
  const status = statusByProperty[position.code] || {};
  const accountingValue = position.values?.accounting?.value_eur || 0;
  const economicValue   = position.values?.economic?.value_eur || accountingValue;
  const mortgage = detail.ing_mortgage_outstanding_eur || 0;
  const isPaidOff = !!detail.ing_mortgage_paid_off;
  const annualRent = (tenant.monthly_rent_eur || 0) * 12;

  let badge = '✓ on time';
  let badgeClass = 'grk-badge-ok';
  if (status.monthsOverdue >= 3) { badge = `❌ ${status.monthsOverdue} months behind`; badgeClass = 'grk-badge-bad'; }
  else if (status.monthsOverdue >= 1) { badge = `⚠ ${status.monthsOverdue} month behind`; badgeClass = 'grk-badge-warn'; }
  else if (status.totalPaid != null) { badge = '✓ on time'; badgeClass = 'grk-badge-ok'; }
  else { badge = '—'; badgeClass = ''; }

  return (
    <div className="grk-prop-card" onClick={onClick}>
      <div className="grk-prop-header">
        <span className="grk-prop-label">{position.label}</span>
        <span className={`grk-prop-badge ${badgeClass}`}>{badge}</span>
      </div>
      <div className="grk-prop-grid">
        <div>
          <div className="grk-prop-k">Book value</div>
          <div className="grk-prop-v">{fmtEur(accountingValue)}</div>
        </div>
        <div>
          <div className="grk-prop-k">Market est.</div>
          <div className="grk-prop-v">{fmtEur(economicValue)}</div>
        </div>
        <div>
          <div className="grk-prop-k">Equity (est.)</div>
          <div className="grk-prop-v">{fmtEur(economicValue - mortgage)}</div>
        </div>
        <div>
          <div className="grk-prop-k">Mortgage</div>
          <div className="grk-prop-v">{isPaidOff ? 'PAID OFF' : fmtEur(mortgage)}</div>
        </div>
        <div>
          <div className="grk-prop-k">Tenant</div>
          <div className="grk-prop-v small">{tenant.name || '—'}</div>
        </div>
        <div>
          <div className="grk-prop-k">Monthly rent</div>
          <div className="grk-prop-v">{tenant.monthly_rent_eur ? fmtEur(tenant.monthly_rent_eur) : '—'}</div>
        </div>
      </div>
      {status.paidUpTo && (
        <div className="grk-prop-status">
          Paid up to <strong>{fmtMonthYear(status.paidUpTo)}</strong>
          {status.owed > 0 && <span className="grk-prop-owed"> · {fmtEur(status.owed)} owed</span>}
          {status.lastPayment && <span className="grk-prop-last"> · last payment {fmtDate(status.lastPayment.date)} ({fmtEur(status.lastPayment.amount)})</span>}
        </div>
      )}
      {(detail.watchlist || []).map((w, i) => (
        <div key={i} className="grk-watchlist-item">⚠ {w.item} — {w.detail}</div>
      ))}
    </div>
  );
}

// ─── PropertyDetailModal — opens when a card is clicked ───────────────────────
function PropertyDetailModal({ position, mortgagesByCode, onClose }) {
  const [tab, setTab] = useState('overview');
  const [txs, setTxs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiGet(`/api/entities/grykrica/transactions?property_code=${position.code}&limit=2000`)
      .then(r => { if (r.success) setTxs(r.transactions || []); })
      .catch(() => setTxs([]))
      .finally(() => setLoading(false));
  }, [position.code]);

  const detail = position.detail || {};
  const tenant = detail.tenant || {};
  const accountingValue = position.values?.accounting?.value_eur || 0;
  const economicValue   = position.values?.economic?.value_eur || accountingValue;

  // Mortgage detail: prefer the mortgage liability position (richer detail) over the
  // property's bare ing_mortgage_outstanding_eur field.
  const mortgagePos = mortgagesByCode?.[detail.ing_mortgage_code];
  const mortgageDetail = mortgagePos?.detail || {};
  const mortgageOutstanding = mortgagePos
    ? (mortgagePos.values?.accounting?.value_eur || 0)
    : (detail.ing_mortgage_outstanding_eur || 0);
  const isPaidOff = !!detail.ing_mortgage_paid_off;
  const monthlyPI = mortgageDetail.monthly_payment_eur || 0;

  const annualRent = (tenant.monthly_rent_eur || 0) * 12;
  const grossYield = economicValue > 0 ? (annualRent / economicValue) * 100 : 0;

  // Rent + extras (12-mo, rich attribution)
  const rentTxs = (txs || []).filter(t => t.kind === 'rent');
  const status = statusFor(rentTxs, tenant);

  // 12-mo cash-flow for this property
  const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - 12);
  const last12 = (txs || []).filter(t => new Date(t.booking_date) >= cutoff);
  const last12By = groupBy(last12, t => t.kind);
  const rent12 = sum(last12By.rent);
  const mortgage12 = sum(last12By.mortgage);
  const syndic12 = sum(last12By.syndic) + sum(last12By.syndic_refund);
  const insurance12 = sum(last12By.insurance);
  const maint12 = sum(last12By.maintenance);
  const tax12 = sum(last12By.tax_ov) + sum(last12By.tax_ven);
  const net12 = rent12 + mortgage12 + syndic12 + insurance12 + maint12 + tax12;

  // Tenant obligations (RV pass-through etc) — track unpaid items
  const unpaidObligations = (detail.tenant_obligations || []).filter(o => o.status === 'unpaid');

  return (
    <ModalOverlay onClose={onClose} className="grk-prop-modal">
      <div className="grk-prop-modal-header">
        <div>
          <h2>{position.label}</h2>
          <div className="grk-prop-modal-sub">{detail.address || ''}</div>
        </div>
        <div className="grk-toggle-group">
          <button className="grk-close-btn" onClick={onClose}>×</button>
        </div>
      </div>

      {/* Key numbers strip — always visible */}
      <div className="grk-keys-strip">
        <div className="grk-key-tile">
          <div className="grk-k">Monthly rent</div>
          <div className="grk-v big">{tenant.monthly_rent_eur ? fmtEur(tenant.monthly_rent_eur) : '—'}</div>
        </div>
        <div className="grk-key-tile">
          <div className="grk-k">Monthly mortgage (P+I)</div>
          <div className="grk-v big">{isPaidOff ? 'PAID OFF' : (monthlyPI ? fmtEur(monthlyPI) : '—')}</div>
        </div>
        <div className="grk-key-tile">
          <div className="grk-k">Net rent − P+I</div>
          <div className={'grk-v big ' + ((tenant.monthly_rent_eur || 0) - monthlyPI >= 0 ? 'grk-amt-pos' : 'grk-amt-neg')}>
            {tenant.monthly_rent_eur ? fmtEur((tenant.monthly_rent_eur || 0) - monthlyPI) : '—'}
          </div>
        </div>
        <div className="grk-key-tile">
          <div className="grk-k">Status</div>
          <div className={'grk-v ' + (status.monthsUnpaid > 0 ? 'grk-warn' : '')}>
            {status.monthsUnpaid > 0 ? `${status.monthsUnpaid} mo unpaid (${fmtEur(status.owed)})` : '✓ all current'}
          </div>
        </div>
      </div>

      {unpaidObligations.length > 0 && (
        <div className="grk-rv-alert">
          ⚠ Tenant obligations unpaid:{' '}
          {unpaidObligations.map((o, i) => (
            <span key={i}>{o.type.replace(/_/g, ' ')} {o.period} = <strong>{fmtEur(o.amount_eur)}</strong>{i < unpaidObligations.length - 1 ? ', ' : ''}</span>
          ))}
        </div>
      )}

      <div className="grk-tabs">
        <button className={tab === 'overview'   ? 'active' : ''} onClick={() => setTab('overview')}>Overview</button>
        <button className={tab === 'tenant'     ? 'active' : ''} onClick={() => setTab('tenant')}>Tenant &amp; rent</button>
        <button className={tab === 'mortgage'   ? 'active' : ''} onClick={() => setTab('mortgage')}>Mortgage</button>
        <button className={tab === 'accounting' ? 'active' : ''} onClick={() => setTab('accounting')}>Accounting</button>
        <button className={tab === 'pl'         ? 'active' : ''} onClick={() => setTab('pl')}>P&amp;L</button>
      </div>

      <div className="grk-modal-body">
        {/* OVERVIEW TAB — economic / property-centric view */}
        {tab === 'overview' && (
          <div>
            <div className="grk-kv-grid">
              <div><div className="grk-k">Market value est.</div><div className="grk-v big">{fmtEur(economicValue)}</div></div>
              <div><div className="grk-k">Mortgage outstanding</div><div className="grk-v">{isPaidOff ? 'PAID OFF' : fmtEur(mortgageOutstanding)}</div></div>
              <div><div className="grk-k">Implied equity in property</div><div className="grk-v big">{fmtEur(economicValue - mortgageOutstanding)}</div></div>
              <div><div className="grk-k">Annual rent (run rate)</div><div className="grk-v">{fmtEur(annualRent)}</div></div>
              <div><div className="grk-k">Gross yield on market</div><div className="grk-v">{grossYield.toFixed(2)}%</div></div>
              <div><div className="grk-k">Yield on equity (gross)</div><div className="grk-v">{(annualRent / Math.max(1, economicValue - mortgageOutstanding) * 100).toFixed(2)}%</div></div>
            </div>
            <div className="grk-section">
              <h4>Property details</h4>
              <div className="grk-kv-grid">
                <div><div className="grk-k">Address</div><div className="grk-v small">{detail.address || '—'}</div></div>
                <div><div className="grk-k">Type</div><div className="grk-v small">{detail.property_type || '—'}</div></div>
                {detail.vme && <div><div className="grk-k">VME</div><div className="grk-v small">{detail.vme}</div></div>}
                {detail.brandpolis_policy && <div><div className="grk-k">Brandpolis</div><div className="grk-v small">{detail.brandpolis_policy}</div></div>}
              </div>
            </div>
            {detail.acquired && (
              <div className="grk-section">
                <h4>Acquisition</h4>
                <div className="grk-kv-grid">
                  <div><div className="grk-k">Date</div><div className="grk-v small">{fmtDate(detail.acquired.date)}</div></div>
                  {detail.acquired.price_eur && <div><div className="grk-k">Price</div><div className="grk-v">{fmtEur(detail.acquired.price_eur)}</div></div>}
                  {detail.acquired.costs_eur && <div><div className="grk-k">Costs (reg+notaris)</div><div className="grk-v">{fmtEur(detail.acquired.costs_eur)}</div></div>}
                  {detail.acquired.total_cash_eur && <div><div className="grk-k">Total cash out</div><div className="grk-v">{fmtEur(detail.acquired.total_cash_eur)}</div></div>}
                  {detail.acquired.notary && <div><div className="grk-k">Notary</div><div className="grk-v small">{detail.acquired.notary}</div></div>}
                  {detail.acquired.structure && <div><div className="grk-k">Structure</div><div className="grk-v small">{detail.acquired.structure}</div></div>}
                  {detail.acquired.seller && <div><div className="grk-k">Seller</div><div className="grk-v small">{detail.acquired.seller}</div></div>}
                  {detail.acquired.encumbrance && <div><div className="grk-k">Encumbrance</div><div className="grk-v small">{detail.acquired.encumbrance}</div></div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ACCOUNTING TAB — cost basis + depreciation + book value (from FY balans) */}
        {tab === 'accounting' && (() => {
          const acc = detail.accounting_detail;
          if (!acc) {
            return <div className="grk-note-info">No detailed accounting breakdown on file for this property yet.</div>;
          }
          const fyLabel    = acc.fiscal_year_end ? new Date(acc.fiscal_year_end).getFullYear() : 'FY';
          const priorLabel = acc.prior_year_end ? new Date(acc.prior_year_end).getFullYear() : 'prior';
          const nbvDelta   = (acc.net_book_value_eur ?? 0) - (acc.net_book_value_prior_eur ?? 0);
          return (
            <div>
              {/* VALUE — clearly split: land / building / costs / gross */}
              <div className="grk-section">
                <h4>Book value — cost (balans {fyLabel})</h4>
                <table className="grk-tx-table grk-acc-table">
                  <thead><tr><th>Component</th><th className="num">Amount</th><th className="small">Ledger</th></tr></thead>
                  <tbody>
                    <tr><td>Land (terrein) — not depreciated</td><td className="num">{fmtEur(acc.land_eur)}</td><td className="small">{acc.ledger_codes?.land || ''}</td></tr>
                    <tr><td>Building (gebouw)</td><td className="num">{fmtEur(acc.building_eur)}</td><td className="small">{acc.ledger_codes?.building || ''}</td></tr>
                    {acc.acquisition_costs_eur > 0 && (
                      <tr><td>Acquisition costs (bijkomende kosten)</td><td className="num">{fmtEur(acc.acquisition_costs_eur)}</td><td className="small">{acc.ledger_codes?.acquisition_costs || ''}</td></tr>
                    )}
                    <tr className="grk-acc-total"><td><strong>Gross cost</strong></td><td className="num"><strong>{fmtEur(acc.gross_cost_eur)}</strong></td><td></td></tr>
                  </tbody>
                </table>
                {acc.capex_during_year_eur > 0 && (
                  <p className="grk-note">Building cost rose {fmtEur(acc.building_eur_prior)} → {fmtEur(acc.building_eur)} in {fyLabel} (+{fmtEur(acc.capex_during_year_eur)} capitalised improvement).</p>
                )}
              </div>

              {/* AFSCHRIJVINGEN — total + annual + diff vs last year */}
              <div className="grk-section">
                <h4>Depreciation (afschrijvingen)</h4>
                <table className="grk-tx-table grk-acc-table">
                  <thead><tr><th></th><th className="num">{fyLabel}</th><th className="num">{priorLabel}</th><th className="num">Δ vs {priorLabel}</th></tr></thead>
                  <tbody>
                    <tr>
                      <td>Accumulated depreciation (total)</td>
                      <td className="num">{fmtEur(-acc.accumulated_depreciation_eur)}</td>
                      <td className="num">{fmtEur(-acc.accumulated_depreciation_prior_eur)}</td>
                      <td className="num grk-amt-neg">{fmtEur(-acc.annual_depreciation_eur)}</td>
                    </tr>
                    <tr>
                      <td>Depreciation charge this year (annual)</td>
                      <td className="num grk-amt-neg">{fmtEur(-acc.annual_depreciation_eur)}</td>
                      <td className="num small" colSpan={2}>≈ {fmtEur(acc.annual_depreciation_eur / 12)}/mo</td>
                    </tr>
                  </tbody>
                </table>
                <p className="grk-note">The annual charge is the year-over-year movement in accumulated depreciation ({fmtEur(acc.accumulated_depreciation_prior_eur)} → {fmtEur(acc.accumulated_depreciation_eur)}).</p>
              </div>

              {/* NET BOOK VALUE + unrealised gain */}
              <div className="grk-section">
                <h4>Net book value</h4>
                <div className="grk-kv-grid">
                  <div><div className="grk-k">Net book value {fyLabel}</div><div className="grk-v big">{fmtEur(acc.net_book_value_eur)}</div></div>
                  <div><div className="grk-k">Net book value {priorLabel}</div><div className="grk-v">{fmtEur(acc.net_book_value_prior_eur)}</div></div>
                  <div><div className="grk-k">Change vs {priorLabel}</div><div className={`grk-v ${nbvDelta < 0 ? 'grk-amt-neg' : 'grk-amt-pos'}`}>{fmtEur(nbvDelta)}</div></div>
                  <div><div className="grk-k">Market estimate (economic)</div><div className="grk-v">{fmtEur(economicValue)}</div></div>
                  <div><div className="grk-k">Implied unrealised gain<br/>(market − book)</div><div className="grk-v big grk-amt-pos">{fmtEur(economicValue - acc.net_book_value_eur)}</div></div>
                </div>
              </div>
              <div className="grk-note-info">From the FY24-25 detailed balans (Eindejaarsafsluiting 30-09-2025). Land is not depreciated; the building (and acquisition costs) depreciate straight-line, so net book value falls each year by the depreciation charge. The implied unrealised gain is value captured economically but not yet realised through a sale.</div>
            </div>
          );
        })()}

        {/* TENANT & RENT TAB */}
        {tab === 'tenant' && (
          <div>
            <div className="grk-section">
              <h4>Current tenant</h4>
              <div className="grk-kv-grid">
                <div><div className="grk-k">Name</div><div className="grk-v">{tenant.name || '—'}</div></div>
                {tenant.profession && <div><div className="grk-k">Profession</div><div className="grk-v small">{tenant.profession}</div></div>}
                <div><div className="grk-k">Monthly rent (current)</div><div className="grk-v">{fmtEur(tenant.monthly_rent_eur)}</div></div>
                <div><div className="grk-k">Annual rent</div><div className="grk-v">{fmtEur(annualRent)}</div></div>
                {tenant.lease_start && <div><div className="grk-k">Lease start</div><div className="grk-v small">{tenant.lease_start}</div></div>}
                {tenant.deposit_eur && <div><div className="grk-k">Deposit held</div><div className="grk-v">{fmtEur(tenant.deposit_eur)}</div></div>}
                {tenant.median_day_of_month && <div><div className="grk-k">Typical pay day</div><div className="grk-v">day {tenant.median_day_of_month}</div></div>}
              </div>
              {tenant.rent_schedule?.length > 1 && (
                <div style={{ marginTop: 8 }}>
                  <div className="grk-k">Rent step history</div>
                  <table className="grk-tx-table" style={{ marginTop: 4 }}>
                    <tbody>
                      {tenant.rent_schedule.map((step, i) => (
                        <tr key={i}>
                          <td className="small">From {step.from}</td>
                          <td>{step.monthly_rent === 0 ? '— (free)' : fmtEur(step.monthly_rent)}/mo</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {tenant.note && <p className="grk-note">{tenant.note}</p>}
            </div>

            <div className="grk-section">
              <h4>Contact</h4>
              <div className="grk-kv-grid">
                <div><div className="grk-k">Email</div><div className="grk-v small">{tenant.contact?.email || <em style={{color:'#9ca3af'}}>not on file</em>}</div></div>
                <div><div className="grk-k">Phone</div><div className="grk-v small">{tenant.contact?.phone || <em style={{color:'#9ca3af'}}>not on file</em>}</div></div>
              </div>
              {tenant.contact?.notes && <p className="grk-note-info">{tenant.contact.notes}</p>}
            </div>

            <div className="grk-section">
              <h4>Payment status — last 12 months</h4>
              <p className="grk-note-info">Each payment matches one specific month. Same-day batches (multiple wires) cover consecutive oldest-unpaid months. Payments outside ±15% of expected rent are tagged as "extras" below.</p>
              {loading ? <p>Loading…</p> : (
                <div className="grk-kv-grid">
                  <div><div className="grk-k">Paid up to</div><div className="grk-v big">{status.paidUpTo ? fmtMonthYear(status.paidUpTo) : '—'}</div></div>
                  <div><div className="grk-k">Currently owed</div><div className={`grk-v big ${status.owed > 0 ? 'grk-warn' : ''}`}>{fmtEur(status.owed)}</div></div>
                  <div><div className="grk-k">Months unpaid</div><div className="grk-v">{status.monthsUnpaid}{status.monthsCurrent ? ` (+${status.monthsCurrent} running)` : ''}</div></div>
                  <div><div className="grk-k">Last payment</div><div className="grk-v small">{status.lastPayment ? `${fmtDate(status.lastPayment.date)} (${fmtEur(status.lastPayment.amount)})` : '—'}</div></div>
                  <div><div className="grk-k">Received (12mo rent)</div><div className="grk-v">{fmtEur(status.totalPaid12mo)}</div></div>
                  <div><div className="grk-k">Expected (12mo total)</div><div className="grk-v">{fmtEur(status.totalExpected12mo)}</div></div>
                </div>
              )}
            </div>

            {!loading && status.months?.length > 0 && (
              <div className="grk-section">
                <h4>Month-by-month grid (12 months)</h4>
                <table className="grk-month-grid">
                  <thead><tr><th>Month</th><th>Expected</th><th>Status</th><th>Paid by</th></tr></thead>
                  <tbody>
                    {status.months.map((m, i) => {
                      const cls =
                        m.status === 'paid'    ? 'grk-mg-paid' :
                        m.status === 'free'    ? 'grk-mg-free' :
                        m.status === 'current' ? 'grk-mg-current' :
                        m.status === 'muted'   ? 'grk-mg-muted' :
                                                 'grk-mg-unpaid';
                      const icon =
                        m.status === 'paid'    ? '✓' :
                        m.status === 'free'    ? '○' :
                        m.status === 'current' ? '⏳' :
                        m.status === 'muted'   ? '–' :
                                                 '⚠';
                      const paidBy = m.paid_by
                        ? `${fmtDate(m.paid_by.booking_date)} · ${fmtEur(m.paid_by.amount_eur, 2)}`
                        : m.status === 'muted'   ? (m.muted_reason || 'past tenant — written off')
                        : m.status === 'free'    ? 'rent-free (verfwerken)'
                        : m.status === 'current' ? 'rent period running'
                        : '—';
                      return (
                        <tr key={i} className={cls}>
                          <td>{m.label}</td>
                          <td>{m.expected === 0 ? '—' : fmtEur(m.expected)}</td>
                          <td>{icon} {m.status}</td>
                          <td className="small">{paidBy}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && status.extras?.length > 0 && (
              <div className="grk-section">
                <h4>Extras / cost recoveries (not rent)</h4>
                <p className="grk-note-info">{status.fromServerLedger ? 'Deposits, OV pass-throughs and cost settlements received from the tenant — tracked separately from rent.' : 'Payments outside the rent-amount tolerance — typically utility recoveries or small adjustments. Tracked separately from rent.'}</p>
                <table className="grk-tx-table">
                  <thead><tr><th>Date</th><th>Amount</th><th>Note</th></tr></thead>
                  <tbody>
                    {status.extras.map((t, i) => (
                      <tr key={t._id || i}>
                        <td>{fmtDate(t.booking_date)}</td>
                        <td className="grk-amt-pos">{fmtEur(t.amount_eur, 2)}</td>
                        <td className="small">{t.classifier_note || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(detail.past_tenants || []).length > 0 && (
              <div className="grk-section">
                <h4>Past tenants</h4>
                <table className="grk-tx-table">
                  <thead><tr><th>Name</th><th>Period</th><th>Monthly rent</th></tr></thead>
                  <tbody>
                    {detail.past_tenants.map((pt, i) => (
                      <tr key={i}>
                        <td>{pt.name}</td>
                        <td className="small">{pt.period}</td>
                        <td>{fmtEur(pt.monthly_rent_eur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* MORTGAGE TAB — rich detail from the mortgage-position record + tax certs */}
        {tab === 'mortgage' && (
          <div>
            {isPaidOff ? (
              <div className="grk-section">
                <h4>Mortgage</h4>
                <p>This property has no active ING mortgage — fully paid off.</p>
                {detail.original_financing_note && <p className="grk-note">{detail.original_financing_note}</p>}
              </div>
            ) : (
              <>
                <div className="grk-section">
                  <h4>Loan terms</h4>
                  <div className="grk-kv-grid">
                    <div><div className="grk-k">Bank</div><div className="grk-v">ING</div></div>
                    <div><div className="grk-k">Contract no</div><div className="grk-v small">{mortgageDetail.ing_contract_no || '—'}</div></div>
                    <div><div className="grk-k">Original principal</div><div className="grk-v">{mortgageDetail.original_principal_eur ? fmtEur(mortgageDetail.original_principal_eur) : '—'}</div></div>
                    <div><div className="grk-k">Loan start</div><div className="grk-v small">{mortgageDetail.loan_start ? fmtDate(mortgageDetail.loan_start) : '—'}</div></div>
                    <div><div className="grk-k">Payoff date</div><div className="grk-v small">{mortgageDetail.loan_end ? fmtDate(mortgageDetail.loan_end) : '—'}</div></div>
                    <div><div className="grk-k">Interest rate (approx)</div><div className="grk-v">{mortgageDetail.interest_rate_approx_pct ? fmtPct(mortgageDetail.interest_rate_approx_pct, 2) : '—'}</div></div>
                  </div>
                </div>
                <div className="grk-section">
                  <h4>Current state &amp; monthly payment</h4>
                  <div className="grk-kv-grid">
                    <div><div className="grk-k">Current outstanding</div><div className="grk-v big">{fmtEur(mortgageOutstanding)}</div></div>
                    <div><div className="grk-k">Outstanding at 30-Sep-2025</div><div className="grk-v">{mortgageDetail.outstanding_eur_at_2025_09_30 ? fmtEur(mortgageDetail.outstanding_eur_at_2025_09_30) : '—'}</div></div>
                    <div><div className="grk-k">% repaid (vs original)</div><div className="grk-v">{mortgageDetail.original_principal_eur ? fmtPct(100 * (1 - mortgageOutstanding / mortgageDetail.original_principal_eur), 1) : '—'}</div></div>
                    <div><div className="grk-k">Monthly P+I total</div><div className="grk-v big">{fmtEur(mortgageDetail.monthly_payment_eur || monthlyPI)}</div></div>
                    <div><div className="grk-k">Of which: principal</div><div className="grk-v">{mortgageDetail.monthly_principal_eur ? fmtEur(mortgageDetail.monthly_principal_eur) + '/mo' : '—'}</div></div>
                    <div><div className="grk-k">Of which: interest</div><div className="grk-v">{mortgageDetail.monthly_interest_eur ? fmtEur(mortgageDetail.monthly_interest_eur) + '/mo' : '—'}</div></div>
                  </div>
                </div>
                <div className="grk-section">
                  <h4>Annual breakdown (per ING Tax Certificate)</h4>
                  <table className="grk-tx-table">
                    <thead><tr><th>Fiscal year (Oct-Sep)</th><th>Principal repaid</th><th>Interest paid</th><th>Total P+I</th></tr></thead>
                    <tbody>
                      {mortgageDetail.fy24_principal_paid_eur != null && (
                        <tr>
                          <td>FY24 (Oct-2023 → Sep-2024)</td>
                          <td className="grk-amt-neg">{fmtEur(-mortgageDetail.fy24_principal_paid_eur, 2)}</td>
                          <td className="grk-amt-neg">{fmtEur(-mortgageDetail.fy24_interest_paid_eur, 2)}</td>
                          <td className="grk-amt-neg">{fmtEur(-(mortgageDetail.fy24_principal_paid_eur + mortgageDetail.fy24_interest_paid_eur), 2)}</td>
                        </tr>
                      )}
                      {mortgageDetail.fy25_principal_paid_eur != null && (
                        <tr>
                          <td>FY25 (Oct-2024 → Sep-2025)</td>
                          <td className="grk-amt-neg">{fmtEur(-mortgageDetail.fy25_principal_paid_eur, 2)}</td>
                          <td className="grk-amt-neg">{fmtEur(-mortgageDetail.fy25_interest_paid_eur, 2)}</td>
                          <td className="grk-amt-neg">{fmtEur(-(mortgageDetail.fy25_principal_paid_eur + mortgageDetail.fy25_interest_paid_eur), 2)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {mortgageDetail.source && <p className="grk-note-info">{mortgageDetail.source}</p>}
                </div>
                <div className="grk-section">
                  <h4>If we sold today at market estimate</h4>
                  <div className="grk-kv-grid">
                    <div><div className="grk-k">Market estimate</div><div className="grk-v">{fmtEur(economicValue)}</div></div>
                    <div><div className="grk-k">− Mortgage to settle</div><div className="grk-v">{fmtEur(-mortgageOutstanding)}</div></div>
                    <div><div className="grk-k">Net equity (before tax)</div><div className="grk-v big">{fmtEur(economicValue - mortgageOutstanding)}</div></div>
                  </div>
                </div>
                {!loading && (() => {
                  const mortTxs = (txs || []).filter(t => t.kind === 'mortgage');
                  if (mortTxs.length === 0) return null;
                  return (
                    <div className="grk-section">
                      <h4>Recent mortgage payments ({mortTxs.length} on file)</h4>
                      <table className="grk-tx-table">
                        <thead><tr><th>Date</th><th>Amount</th></tr></thead>
                        <tbody>
                          {mortTxs.slice(0, 12).map((t, i) => (
                            <tr key={t._id || i}>
                              <td>{fmtDate(t.booking_date)}</td>
                              <td className="grk-amt-neg">{fmtEur(t.amount_eur, 2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* P&L TAB */}
        {tab === 'pl' && (
          <div>
            <div className="grk-section">
              <h4>Last 12 months — per property cash flow</h4>
              {loading ? <p>Loading…</p> : (
                <table className="grk-tx-table">
                  <tbody>
                    <tr><td>Rent received</td><td className="grk-amt-pos">{fmtEur(rent12)}</td></tr>
                    <tr><td>Mortgage payments</td><td className="grk-amt-neg">{fmtEur(mortgage12)}</td></tr>
                    <tr><td>Syndic (net of refunds)</td><td className={syndic12 < 0 ? 'grk-amt-neg' : 'grk-amt-pos'}>{fmtEur(syndic12)}</td></tr>
                    <tr><td>Insurance</td><td className="grk-amt-neg">{fmtEur(insurance12)}</td></tr>
                    <tr><td>Maintenance</td><td className="grk-amt-neg">{fmtEur(maint12)}</td></tr>
                    <tr><td>Tax (OV + VEN)</td><td className="grk-amt-neg">{fmtEur(tax12)}</td></tr>
                    <tr className="grk-total-row"><td>Net cash flow</td><td className={net12 >= 0 ? 'grk-amt-pos' : 'grk-amt-neg'}>{fmtEur(net12)}</td></tr>
                  </tbody>
                </table>
              )}
            </div>
            {!loading && last12.length > 0 && (
              <div className="grk-section">
                <h4>All linked transactions (last 12 months, {last12.length} total)</h4>
                <table className="grk-tx-table">
                  <thead><tr><th>Date</th><th>Amount</th><th>Kind</th><th>Note</th></tr></thead>
                  <tbody>
                    {last12.slice(0, 60).map((t, i) => (
                      <tr key={t._id || i}>
                        <td className="small">{fmtDate(t.booking_date)}</td>
                        <td className={t.amount_eur > 0 ? 'grk-amt-pos' : 'grk-amt-neg'}>{fmtEur(t.amount_eur, 2)}</td>
                        <td className="small">{t.kind}</td>
                        <td className="small">{(t.classifier_note || '').slice(0, 80)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

// ─── Main popup ───────────────────────────────────────────────────────────────
function GryKriCaPopup({ onClose }) {
  const [view, setView]   = useState('economic');
  const [tab, setTab]     = useState('properties');
  const [data, setData]   = useState(null);
  const [meta, setMeta]   = useState(null);
  const [statusByProperty, setStatusByProperty] = useState({});
  const [openProperty, setOpenProperty] = useState(null);
  const [allTxs, setAllTxs] = useState([]);   // for forecast
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/api/entities/grykrica/view?view=${view}`);
      if (!r.success) throw new Error(r.message);
      setMeta(r.meta);
      setData(r.view);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [view]);
  useEffect(() => { load(); }, [load]);

  // Fetch transactions for all properties (for the at-a-glance badges) + a
  // full-window pull for the recurring-expense forecast.
  useEffect(() => {
    if (!data?.positions) return;
    const props = data.positions.filter(p => p.category === 'real_estate');
    Promise.all(
      props.map(p =>
        apiGet(`/api/entities/grykrica/transactions?property_code=${p.code}&kind=rent&limit=500`)
          .then(r => {
            if (!r.success) return null;
            const s = statusFor(r.transactions, p.detail?.tenant);
            return [p.code, s];
          })
          .catch(() => null)
      )
    ).then(results => {
      const out = {};
      for (const r of results) { if (r) out[r[0]] = r[1]; }
      setStatusByProperty(out);
    });
    // Fetch the last 36 months of all-tx for forecast (covers 3 cycles of annual items)
    const from = new Date(); from.setMonth(from.getMonth() - 36);
    apiGet(`/api/entities/grykrica/transactions?from=${from.toISOString()}&limit=5000`)
      .then(r => { if (r.success) setAllTxs(r.transactions || []); })
      .catch(() => {});
  }, [data]);

  const properties = useMemo(
    () => (data?.positions || []).filter(p => p.category === 'real_estate'),
    [data]
  );
  const mortgagesByCode = useMemo(() => {
    const out = {};
    for (const p of (data?.positions || [])) {
      if (p.category === 'mortgage') out[p.code] = p;
    }
    return out;
  }, [data]);

  const totals = data?.totals || {};
  const kristofShare = (totals.equity_eur || 0) * 0.5;

  // Outstanding items: arrears + tenant obligations + watch + forecast
  const outstanding = useMemo(() => {
    const items = [];
    for (const p of properties) {
      const s = statusByProperty[p.code];
      if (s?.owed > 0 && s?.monthsUnpaid >= 1) {
        items.push({
          type: 'arrears',
          property: p.label,
          owed: s.owed,
          months: s.monthsUnpaid,
          paidUpTo: s.paidUpTo,
          tenant: p.detail?.tenant?.name,
        });
      }
      // Tenant obligations (RV pass-through etc.) that are unpaid
      for (const o of (p.detail?.tenant_obligations || [])) {
        if (o.status === 'unpaid') {
          items.push({
            type: 'tenant_obligation',
            property: p.label,
            tenant: p.detail?.tenant?.name,
            obligation_label: (o.type || 'obligation').replace(/_/g, ' '),
            period: o.period,
            amount: o.amount_eur,
            note: o.note,
          });
        }
      }
      for (const w of (p.detail?.watchlist || [])) {
        items.push({ type: 'watch', property: p.label, watch: w });
      }
    }
    // Append Phase-4 recurring-expense forecast
    for (const f of predictUpcoming(allTxs)) {
      items.push({ type: 'forecast', ...f });
    }
    return items;
  }, [properties, statusByProperty, allTxs]);

  // Balance sheet grouping
  const bsGrouped = useMemo(() => {
    if (!data?.positions) return null;
    const assets  = data.positions.filter(p => p.ledger_side === 'asset');
    const liabs   = data.positions.filter(p => p.ledger_side === 'liability');
    const equity  = data.positions.filter(p => p.ledger_side === 'equity');
    return { assets, liabs, equity };
  }, [data]);

  if (loading) return <ModalOverlay onClose={onClose}><div className="grk-loading">Loading GryKriCa…</div></ModalOverlay>;
  if (error)   return <ModalOverlay onClose={onClose}><div className="grk-error">Error: {error}</div></ModalOverlay>;

  return (
    <ModalOverlay onClose={onClose}>
      <div className="grk-header">
        <div>
          <h2>{meta?.name || 'GryKriCa Comm.V'}</h2>
          <div className="grk-sub">
            {(meta?.owners || []).map(o => `${o.person} (${o.share_pct}%)`).join('  ·  ')}
          </div>
        </div>
        <div className="grk-toggle-group">
          <button className={view === 'accounting' ? 'active' : ''} onClick={() => setView('accounting')}>Accounting</button>
          <button className={view === 'economic'   ? 'active' : ''} onClick={() => setView('economic')}>Economic</button>
          <SeeFilesButton url={getDriveFolderUrl('grykrica')} />
          <button className="grk-close-btn" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="grk-headline-row">
        <div className="grk-headline">
          <div className="grk-k">Total equity ({view})</div>
          <div className="grk-v big">{fmtEur(totals.equity_eur)}</div>
        </div>
        <div className="grk-headline">
          <div className="grk-k">Assets</div>
          <div className="grk-v">{fmtEur(totals.assets_eur)}</div>
        </div>
        <div className="grk-headline">
          <div className="grk-k">Liabilities</div>
          <div className="grk-v">{fmtEur(totals.liabilities_eur)}</div>
        </div>
        <div className="grk-headline">
          <div className="grk-k">Kristof's 50% share</div>
          <div className="grk-v big grk-hl-share">{fmtEur(kristofShare)}</div>
        </div>
      </div>

      {outstanding.length > 0 && (
        <div className="grk-outstanding-panel">
          <h4>Outstanding items ({outstanding.length})</h4>
          {outstanding.map((o, i) => {
            if (o.type === 'arrears') {
              return (
                <div key={i} className="grk-outstanding-item grk-out-arrears">
                  <span className="grk-out-icon">⚠</span>
                  <strong>{o.property}</strong> — {o.tenant}: {o.months} month{o.months !== 1 ? 's' : ''} unpaid, <strong>{fmtEur(o.owed)}</strong> owed
                  <span className="small"> · paid up to {fmtMonthYear(o.paidUpTo)}</span>
                </div>
              );
            }
            if (o.type === 'tenant_obligation') {
              return (
                <div key={i} className="grk-outstanding-item grk-out-arrears">
                  <span className="grk-out-icon">💰</span>
                  <strong>{o.property}</strong> — {o.tenant} owes <strong>{fmtEur(o.amount)}</strong> ({o.obligation_label} {o.period})
                  {o.note && <span className="small"> · {o.note}</span>}
                </div>
              );
            }
            if (o.type === 'watch') {
              return (
                <div key={i} className="grk-outstanding-item grk-out-watch">
                  <span className="grk-out-icon">📋</span>
                  <strong>{o.property}</strong> — {o.watch.item} <span className="small">({o.watch.detail})</span>
                </div>
              );
            }
            // forecast
            return (
              <div key={i} className="grk-outstanding-item grk-out-forecast">
                <span className="grk-out-icon">📅</span>
                <strong>{o.label}</strong> — expected {fmtDate(o.date)} (~{fmtEur(o.amount)})
                <span className="small"> · {o.note}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="grk-tabs">
        <button className={tab === 'properties' ? 'active' : ''} onClick={() => setTab('properties')}>Properties</button>
        <button className={tab === 'balance'    ? 'active' : ''} onClick={() => setTab('balance')}>Balance sheet</button>
        <button className={tab === 'cashflow'   ? 'active' : ''} onClick={() => setTab('cashflow')}>Cash & P&L</button>
      </div>

      <div className="grk-body">
        {tab === 'properties' && (
          <div>
            {properties.map(p => (
              <PropertyCard
                key={p.code}
                position={p}
                view={view}
                statusByProperty={statusByProperty}
                onClick={() => setOpenProperty(p)}
              />
            ))}
          </div>
        )}

        {tab === 'balance' && bsGrouped && (
          <div className="grk-bs-grid">
            <div className="grk-bs-col">
              <h4>Assets</h4>
              {bsGrouped.assets.map(p => (
                <div key={p.code} className="grk-bs-row" onClick={() => p.category === 'real_estate' && setOpenProperty(p)}>
                  <span className={p.category === 'real_estate' ? 'grk-bs-clickable' : ''}>{p.label}</span>
                  <span>{fmtEur(view === 'economic' ? (p.values?.economic?.value_eur || p.values?.accounting?.value_eur) : p.values?.accounting?.value_eur)}</span>
                </div>
              ))}
              <div className="grk-bs-row grk-bs-total"><span>Total assets</span><span>{fmtEur(totals.assets_eur)}</span></div>
            </div>
            <div className="grk-bs-col">
              <h4>Liabilities</h4>
              {bsGrouped.liabs.map(p => (
                <div key={p.code} className="grk-bs-row">
                  <span>{p.label}</span>
                  <span>{fmtEur(p.values?.accounting?.value_eur)}</span>
                </div>
              ))}
              <div className="grk-bs-row grk-bs-total"><span>Total liabilities</span><span>{fmtEur(totals.liabilities_eur)}</span></div>
              <h4 style={{ marginTop: 20 }}>Equity</h4>
              {bsGrouped.equity.map(p => (
                <div key={p.code} className="grk-bs-row">
                  <span>{p.label}</span>
                  <span>{fmtEur(p.values?.accounting?.value_eur)}</span>
                </div>
              ))}
              <div className="grk-bs-row grk-bs-total"><span>Total equity</span><span>{fmtEur(totals.equity_eur)}</span></div>
            </div>
          </div>
        )}

        {tab === 'cashflow' && <CashflowTab properties={properties} />}
      </div>

      {openProperty && (
        <PropertyDetailModal
          position={openProperty}
          mortgagesByCode={mortgagesByCode}
          onClose={() => setOpenProperty(null)}
        />
      )}
    </ModalOverlay>
  );
}

// ─── Cash & P&L tab — with timeframe + property + kind filters ────────────────
const TIMEFRAMES = [
  { value: '1m',  label: 'Past month',   months: 1   },
  { value: '3m',  label: 'Past 3 months', months: 3  },
  { value: '12m', label: 'Past 12 months', months: 12 },
  { value: '2y',  label: 'Past 2 years', months: 24 },
  { value: '3y',  label: 'Past 3 years', months: 36 },
  { value: 'all', label: 'All time',     months: null },
];

function CashflowTab({ properties }) {
  const [timeframe, setTimeframe]   = useState('12m');
  const [propertyCode, setProperty] = useState('');  // '' = all
  const [kind, setKind]             = useState('');  // '' = all
  const [summary, setSummary] = useState(null);
  const [recent, setRecent]   = useState([]);
  const [loading, setLoading] = useState(true);

  // Compute `from` date based on timeframe selection
  const fromDate = useMemo(() => {
    const tf = TIMEFRAMES.find(t => t.value === timeframe) || TIMEFRAMES[2];
    if (tf.months == null) return null;
    const d = new Date();
    d.setMonth(d.getMonth() - tf.months);
    return d;
  }, [timeframe]);

  useEffect(() => {
    setLoading(true);
    const buildParams = (extra = {}) => {
      const p = new URLSearchParams();
      if (fromDate) p.set('from', fromDate.toISOString());
      if (propertyCode) p.set('property_code', propertyCode);
      if (kind) p.set('kind', kind);
      Object.entries(extra).forEach(([k, v]) => p.set(k, v));
      return p.toString();
    };
    Promise.all([
      apiGet(`/api/entities/grykrica/cashflow-summary?${buildParams()}`),
      apiGet(`/api/entities/grykrica/transactions?${buildParams({ limit: 100 })}`),
    ]).then(([s, r]) => {
      if (s.success) setSummary(s);
      if (r.success) setRecent(r.transactions || []);
    }).finally(() => setLoading(false));
  }, [timeframe, propertyCode, kind, fromDate]);

  const inflows  = (summary?.by_kind || []).filter(k => k.total_eur > 0);
  const outflows = (summary?.by_kind || []).filter(k => k.total_eur < 0);
  const net = (summary?.by_kind || []).reduce((s, k) => s + k.total_eur, 0);

  // All known kinds for the kind filter dropdown
  const allKinds = [
    'rent', 'mortgage', 'syndic', 'syndic_refund', 'tax_ov', 'tax_ven', 'tax_btw',
    'accountant', 'insurance', 'maintenance', 'social_security', 'admin_fee',
    'rp_loan_in', 'rp_loan_out', 'tenant_deposit_in', 'tenant_deposit_out',
    'utility_reimbursement', 'bank_fee', 'past_acquisition', 'past_sale', 'past_burst_rent',
    'informational', 'other_in', 'other_out',
  ];

  const activeFilters = [];
  if (propertyCode) activeFilters.push(`property ${propertyCode}`);
  if (kind)         activeFilters.push(`kind ${kind}`);

  return (
    <div>
      <div className="grk-filters">
        <div className="grk-filter">
          <label>Time frame</label>
          <select value={timeframe} onChange={e => setTimeframe(e.target.value)}>
            {TIMEFRAMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="grk-filter">
          <label>Property</label>
          <select value={propertyCode} onChange={e => setProperty(e.target.value)}>
            <option value="">All properties</option>
            {properties?.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
          </select>
        </div>
        <div className="grk-filter">
          <label>Kind</label>
          <select value={kind} onChange={e => setKind(e.target.value)}>
            <option value="">All kinds</option>
            {allKinds.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        {(propertyCode || kind) && (
          <button className="grk-filter-clear" onClick={() => { setProperty(''); setKind(''); }}>
            Clear filters
          </button>
        )}
      </div>

      {loading ? <p style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading…</p> : (
        <>
          <MonthlyFlowChart transactions={recent} fromDate={fromDate} />
          <div className="grk-section">
            <h4>
              By kind — {(TIMEFRAMES.find(t => t.value === timeframe) || {}).label}
              {activeFilters.length > 0 && <span className="grk-filter-label"> · filtered by {activeFilters.join(', ')}</span>}
            </h4>
            <div className="grk-bs-grid">
              <div className="grk-bs-col">
                <h5>Inflows ({inflows.length} kinds, {sum(inflows, 'count')} tx)</h5>
                {inflows.length === 0 && <div className="grk-empty">No inflows in this view</div>}
                {inflows.map(k => (
                  <div key={k._id} className="grk-bs-row">
                    <span>{k._id} <span className="small">×{k.count}</span></span>
                    <span className="grk-amt-pos">{fmtEur(k.total_eur)}</span>
                  </div>
                ))}
                {inflows.length > 0 && <div className="grk-bs-row grk-bs-total"><span>Total in</span><span className="grk-amt-pos">{fmtEur(sum(inflows, 'total_eur'))}</span></div>}
              </div>
              <div className="grk-bs-col">
                <h5>Outflows ({outflows.length} kinds, {sum(outflows, 'count')} tx)</h5>
                {outflows.length === 0 && <div className="grk-empty">No outflows in this view</div>}
                {outflows.map(k => (
                  <div key={k._id} className="grk-bs-row">
                    <span>{k._id} <span className="small">×{k.count}</span></span>
                    <span className="grk-amt-neg">{fmtEur(k.total_eur)}</span>
                  </div>
                ))}
                {outflows.length > 0 && <div className="grk-bs-row grk-bs-total"><span>Total out</span><span className="grk-amt-neg">{fmtEur(sum(outflows, 'total_eur'))}</span></div>}
              </div>
            </div>
            <div className="grk-net-row">
              NET: <strong className={net >= 0 ? 'grk-amt-pos' : 'grk-amt-neg'}>{fmtEur(net)}</strong>
            </div>
          </div>

          <div className="grk-section">
            <h4>Transactions ({recent.length} most recent)</h4>
            <table className="grk-tx-table">
              <thead><tr><th>Date</th><th>Amount</th><th>Kind</th><th>Property</th><th>Note</th></tr></thead>
              <tbody>
                {recent.length === 0 && <tr><td colSpan="5" className="grk-empty">No transactions in this view</td></tr>}
                {recent.map((t, i) => (
                  <tr key={t._id || i}>
                    <td className="small">{fmtDate(t.booking_date)}</td>
                    <td className={t.amount_eur > 0 ? 'grk-amt-pos' : 'grk-amt-neg'}>{fmtEur(t.amount_eur, 2)}</td>
                    <td className="small">{t.kind}</td>
                    <td className="small">{t.property_code || '—'}</td>
                    <td className="small">{(t.classifier_note || '').slice(0, 70)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Monthly inflow/outflow chart (SVG) for Cash & P&L tab ──────────────────
function MonthlyFlowChart({ transactions, fromDate }) {
  if (!transactions || transactions.length === 0) return null;

  // Group by year-month
  const buckets = {};
  let minDate = null, maxDate = null;
  for (const tx of transactions) {
    const d = new Date(tx.booking_date);
    if (!minDate || d < minDate) minDate = d;
    if (!maxDate || d > maxDate) maxDate = d;
  }
  if (!minDate) return null;
  if (fromDate && fromDate > minDate) minDate = fromDate;
  // Build month buckets between min and max
  const months = [];
  const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  while (cursor <= end) {
    const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    months.push({ key, year: cursor.getFullYear(), month: cursor.getMonth(),
                  label: cursor.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
                  inflow: 0, outflow: 0 });
    buckets[key] = months[months.length - 1];
    cursor.setMonth(cursor.getMonth() + 1);
  }
  for (const tx of transactions) {
    const d = new Date(tx.booking_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const b = buckets[key];
    if (!b) continue;
    if (tx.amount_eur > 0) b.inflow += tx.amount_eur;
    else                    b.outflow += Math.abs(tx.amount_eur);
  }

  // Chart dimensions
  const W = 720, H = 220, padT = 20, padB = 40, padL = 60, padR = 10;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const barGroup = innerW / months.length;
  const barW = Math.max(2, barGroup * 0.4);

  const maxVal = Math.max(1, ...months.map(m => Math.max(m.inflow, m.outflow)));
  const yScale = v => innerH * (v / maxVal);

  // Y-axis tick values: nice rounded
  const tickStep = Math.pow(10, Math.floor(Math.log10(maxVal))) * (maxVal / Math.pow(10, Math.floor(Math.log10(maxVal))) > 3 ? 2 : 1);
  const ticks = [];
  for (let v = 0; v <= maxVal * 1.05; v += tickStep) ticks.push(v);

  return (
    <div className="grk-section">
      <h4>Monthly cash flow</h4>
      <p className="grk-note-info">Stacked bars: green = inflows received in that month, red = outflows. Hover for details.</p>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ background: '#fafbfc', borderRadius: 6, border: '1px solid #e5e7eb' }}>
        {/* Y axis ticks + grid */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={padT + innerH - yScale(t)} y2={padT + innerH - yScale(t)} stroke="#e5e7eb" strokeDasharray="2,2" />
            <text x={padL - 6} y={padT + innerH - yScale(t) + 4} textAnchor="end" fontSize="9" fill="#6b7280">€{(t/1000).toFixed(0)}k</text>
          </g>
        ))}
        {/* Bars */}
        {months.map((m, i) => {
          const xCenter = padL + i * barGroup + barGroup / 2;
          const inflowH = yScale(m.inflow);
          const outflowH = yScale(m.outflow);
          return (
            <g key={m.key}>
              <rect x={xCenter - barW - 1} y={padT + innerH - inflowH} width={barW} height={inflowH} fill="#10b981" opacity="0.85">
                <title>{m.label}: +€{m.inflow.toFixed(0)} in</title>
              </rect>
              <rect x={xCenter + 1} y={padT + innerH - outflowH} width={barW} height={outflowH} fill="#ef4444" opacity="0.85">
                <title>{m.label}: −€{m.outflow.toFixed(0)} out</title>
              </rect>
              {i % Math.max(1, Math.floor(months.length / 12)) === 0 && (
                <text x={xCenter} y={padT + innerH + 14} textAnchor="middle" fontSize="9" fill="#6b7280">{m.label}</text>
              )}
            </g>
          );
        })}
        {/* X axis line */}
        <line x1={padL} x2={W - padR} y1={padT + innerH} y2={padT + innerH} stroke="#374151" strokeWidth="0.5" />
      </svg>
      <div className="grk-chart-legend">
        <span><span className="grk-chip grk-chip-in"></span> inflows</span>
        <span><span className="grk-chip grk-chip-out"></span> outflows</span>
        <span style={{marginLeft: 12}}>Showing {months.length} months · {transactions.length} transactions</span>
      </div>
    </div>
  );
}

// ─── Phase 4 — recurring-expense forecast ─────────────────────────────────────
// Predicts upcoming expenses in the next 60 days based on classifier history.
// Used by Outstanding items panel.
function predictUpcoming(transactions, today = new Date()) {
  const out = [];
  const horizon = new Date(today); horizon.setDate(today.getDate() + 60);

  // Recurring debits by kind/counterparty — pick the most recent of each, add 1 cadence
  const recurring = [
    { kind: 'tax_ov',         cadence_months: 12, label: 'Onroerende voorheffing', expected_eur: 5500 },
    { kind: 'insurance',      cadence_months: 12, label: 'Brandpolis P&V/Vivium',   expected_eur: 1278 },
    { kind: 'accountant',     cadence_months: 12, label: 'VDS accountant fee',      expected_eur: 2565 },
    { kind: 'social_security',cadence_months: 12, label: 'Xerius sociale bijdrage', expected_eur: 400 },
  ];
  for (const r of recurring) {
    const matches = (transactions || []).filter(t => t.kind === r.kind && t.amount_eur < 0)
      .sort((a, b) => new Date(b.booking_date) - new Date(a.booking_date));
    if (matches.length === 0) continue;
    const last = matches[0];
    const next = new Date(last.booking_date);
    next.setMonth(next.getMonth() + r.cadence_months);
    if (next <= horizon && next > today) {
      out.push({
        kind: 'forecast',
        label: r.label,
        date: next,
        amount: r.expected_eur,
        note: `Last paid ${fmtDate(last.booking_date)} — ${r.cadence_months}mo cadence`,
      });
    }
  }
  return out.sort((a, b) => a.date - b.date);
}

export default GryKriCaPopup;
